// Admin API client. Always uses the SAME-ORIGIN path `/api/*`:
//
//   - dev:  Vite proxies /api/* → http://localhost:8787 (DEV_ADMIN_BYPASS=1)
//   - prod: admin.fuelio.net's Pages Function (functions/api/[[path]].ts)
//           forwards to api.fuelio.net, preserving the `Cf-Access-Jwt-Assertion`
//           header that CF Access injects at the edge.
//
// Calling api.fuelio.net cross-origin from the SPA would NOT work: the
// `CF_Authorization` cookie is scoped to admin.fuelio.net, so the cross-
// subdomain request reaches api.fuelio.net unauthenticated and 401s.

export const API_BASE = '/api'

export class ApiError extends Error {
  constructor(public status: number, public payload: unknown) {
    super(`api_error_${status}`)
  }
}

/**
 * Format an admin-side error for display. Reads ApiError.payload.error first
 * (server-supplied stable code), maps known codes to friendly Chinese text,
 * and falls back to status / message for unmapped cases.
 */
const ADMIN_ERROR_MESSAGES: Record<string, string> = {
  sku_taken: 'SKU 已存在，换一个',
  empty_patch: '没有改动需要保存',
  product_not_found: '商品不存在',
  user_not_found: '用户不存在',
  order_not_found: '订单不存在',
  invalid_input: '输入不合法',
  missing_access_token: '管理员凭证缺失，请重新登录',
  invalid_access_token: '管理员凭证无效',
  admin_misconfigured: '管理员入口未配置（联系运维）',
}

/** Rewrite an absolute api.fuelio.net upload URL to a same-origin path so the
 *  browser fetches via the admin's /api proxy. Reasons:
 *    1. Some users' local network setups (corporate proxy, Clash routing,
 *       region restrictions) intermittently break direct connections to
 *       api.fuelio.net while pages.dev / admin.fuelio.net still work.
 *    2. Same-origin keeps the request inside Cloudflare's edge — slightly
 *       faster and hits the same cache as other admin assets.
 *
 *  Inputs we tolerate:
 *    - "https://api.fuelio.net/uploads/foo.png"  → "/api/uploads/foo.png"
 *    - "/api/uploads/foo.png" or "/uploads/foo.png" → unchanged
 *    - empty / undefined → empty string
 *  Anything else (e.g. an unrelated CDN) passes through untouched.
 */
export function imageDisplayUrl(stored: string | null | undefined): string {
  if (!stored) return ''
  if (stored.startsWith('/')) return stored
  return stored.replace(/^https?:\/\/api\.fuelio\.net/, API_BASE)
}

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    const payload = e.payload as { error?: string; success?: boolean; message?: string } | null | undefined
    const code = payload?.error
    if (code && typeof code === 'string' && ADMIN_ERROR_MESSAGES[code]) return ADMIN_ERROR_MESSAGES[code]
    if (code && typeof code === 'string') return `${code}（HTTP ${e.status}）`
    // zValidator 失败时 payload 形如 { success:false, error:{ issues:[{message,path}], name:'ZodError' } }
    // 不解开就会被 ADMIN_ERROR_MESSAGES 漏掉、然后 stringify 成 "[object Object]"。
    const zod = (payload as any)?.error
    if (zod && typeof zod === 'object' && Array.isArray(zod.issues)) {
      const first = zod.issues[0]
      if (first?.message) {
        const field = Array.isArray(first.path) ? first.path.join('.') : ''
        return field ? `${field}: ${first.message}` : first.message
      }
    }
    if (payload?.message) return payload.message
    return `请求失败（HTTP ${e.status}）`
  }
  if (e instanceof Error) return e.message
  return String(e)
}

async function request<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const res = await fetch(API_BASE + path, {
    credentials: 'include',
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(opts.headers ?? {}),
    },
  })
  const text = await res.text()
  let parsed: unknown = null
  let parseFailed = false
  try {
    parsed = text ? JSON.parse(text) : null
  } catch {
    parseFailed = true
    parsed = text
  }
  if (!res.ok) {
    // 401 = admin cookie 缺失 / 过期 / 篡改 → 强制回登录。
    // 不在 /login 上时才跳；/auth/* 自身请求也不跳，避免 admin-login 失败时
    // 被 request() 直接覆盖前端的错误提示。
    if (
      res.status === 401 &&
      typeof window !== 'undefined' &&
      !window.location.pathname.startsWith('/login') &&
      !path.startsWith('/auth/')
    ) {
      window.location.href = '/login'
    }
    throw new ApiError(res.status, parsed)
  }
  // 2xx but body wasn't JSON ⇒ the request likely fell through to /index.html
  // (e.g. /api/* Pages-Functions proxy not deployed). Returning the HTML
  // string masqueraded as `T` causes downstream `Object.values(undefined)`
  // / `.map of undefined` crashes that look like data bugs but are really
  // a routing fault. Surface it as a real error instead.
  if (parseFailed) {
    throw new ApiError(res.status, {
      error: 'invalid_json_response',
      hint: 'Pages /api proxy may be missing — re-deploy admin so functions/api/[[path]].ts ships with the build.',
      sample: typeof parsed === 'string' ? parsed.slice(0, 200) : null,
    })
  }
  return parsed as T
}

// ── Admin auth helpers ───────────────────────────────────────────
// 双轨：CF Access（super admin）/ 邮箱+密码|验证码（普通管理员）。
// httpOnly cookie 由后端写入，前端 JS 拿不到。

export type AdminLoginInput =
  | { email: string; password: string }
  | { email: string; code: string }

export async function adminLogin(input: AdminLoginInput) {
  return request<{ user: { id: string; email: string; role: string } }>(
    '/auth/admin-login',
    { method: 'POST', body: JSON.stringify(input) },
  )
}

export async function sendAdminLoginCode(email: string) {
  // 复用普通用户的 /auth/send-code（同一份 KV code 池），admin-login
  // 接收时再校验 user.role==='admin'。
  return request<{ ok: true }>('/auth/send-code', {
    method: 'POST',
    body: JSON.stringify({ email }),
  })
}

export async function adminLogout() {
  return request<{ ok: true }>('/auth/admin-logout', { method: 'POST' })
}

export const api = {
  whoami: () => request<{ admin: { email: string; sub: string; via: string } }>('/admin/whoami'),
  stats: () =>
    request<{
      users: { total: number }
      products: { active: number }
      orders: Record<string, number>
      revenue: { grossPaidCents: number; grossLifetimeCents: number }
    }>('/admin/stats'),

  listProducts: () =>
    request<{ products: AdminProduct[] }>('/admin/products'),
  createProduct: (body: ProductCreateInput) =>
    request<{ id: string }>('/admin/products', { method: 'POST', body: JSON.stringify(body) }),
  patchProduct: (id: string, body: Partial<ProductCreateInput>) =>
    request<{ ok: true }>(`/admin/products/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  // Hard delete. Admin UI should confirm first — this loses the product row,
  // and any past order_items that reference it become orphan refs.
  deleteProduct: (id: string) =>
    request<{ ok: true }>(`/admin/products/${id}`, { method: 'DELETE' }),

  listUsers: (q: { q?: string; status?: string; limit?: number; offset?: number } = {}) => {
    const qs = new URLSearchParams()
    if (q.q) qs.set('q', q.q)
    if (q.status) qs.set('status', q.status)
    if (q.limit !== undefined) qs.set('limit', String(q.limit))
    if (q.offset !== undefined) qs.set('offset', String(q.offset))
    const suffix = qs.toString() ? '?' + qs.toString() : ''
    return request<{ users: AdminUser[]; total: number }>('/admin/users' + suffix)
  },
  patchUser: (id: string, body: Partial<Pick<AdminUser, 'status' | 'displayName' | 'locale' | 'role'>>) =>
    request<{ ok: true }>(`/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  adjustUserWallet: (id: string, body: { deltaCents: number; note: string }) =>
    request<{ ok: true; beforeCents: number; afterCents: number; appliedDeltaCents: number }>(
      `/admin/users/${id}/wallet/adjust`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  // 管理员代设新密码 —— 服务端校验 8~128 位，不发任何通知邮件，事件审计落库
  setUserPassword: (id: string, password: string) =>
    request<{ ok: true }>(`/admin/users/${id}/password`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),

  listOrders: (q: { q?: string; status?: string; limit?: number; offset?: number } = {}) => {
    const qs = new URLSearchParams()
    if (q.q) qs.set('q', q.q)
    if (q.status) qs.set('status', q.status)
    if (q.limit !== undefined) qs.set('limit', String(q.limit))
    if (q.offset !== undefined) qs.set('offset', String(q.offset))
    const suffix = qs.toString() ? '?' + qs.toString() : ''
    return request<{ orders: AdminOrderRow[]; total: number }>('/admin/orders' + suffix)
  },
  getOrder: (id: string) =>
    request<{
      order: AdminOrderRow & { metadata: unknown; paymentIntentId: string | null }
      items: AdminOrderItem[]
    }>(`/admin/orders/${id}`),
  patchOrder: (id: string, body: { status?: string; note?: string }) =>
    request<{ ok: true }>(`/admin/orders/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  // Policy templates: shared content blocks (delivery / address / notice).
  listPolicyTemplates: (type?: PolicyTemplateType) => {
    const suffix = type ? `?type=${encodeURIComponent(type)}` : ''
    return request<{ templates: PolicyTemplateRow[] }>('/admin/policy-templates' + suffix)
  },
  getPolicyTemplate: (id: string) =>
    request<{ template: PolicyTemplateRow }>(`/admin/policy-templates/${id}`),
  createPolicyTemplate: (body: PolicyTemplateCreateInput) =>
    request<{ id: string }>('/admin/policy-templates', { method: 'POST', body: JSON.stringify(body) }),
  patchPolicyTemplate: (id: string, body: PolicyTemplatePatchInput) =>
    request<{ ok: true }>(`/admin/policy-templates/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deletePolicyTemplate: (id: string) =>
    request<{ ok: true }>(`/admin/policy-templates/${id}`, { method: 'DELETE' }),

  // Dictionaries (variant / cat / brand). Generic CRUD over the dict_items
  // table — the same admin screen drives all three keys.
  listDict: (key?: string) => {
    const suffix = key ? `?key=${encodeURIComponent(key)}` : ''
    return request<{ items: DictItem[] }>('/admin/dict' + suffix)
  },
  createDict: (body: DictCreateInput) =>
    request<{ id: string }>('/admin/dict', { method: 'POST', body: JSON.stringify(body) }),
  patchDict: (id: string, body: DictPatchInput) =>
    request<{ ok: true }>(`/admin/dict/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  // Soft-delete: server flips active=0 so historical metadata still resolves.
  deactivateDict: (id: string) =>
    request<{ ok: true }>(`/admin/dict/${id}`, { method: 'DELETE' }),

  // Menus (presentation overlay). Code-defined routes; admin can rename,
  // toggle visible/active, and reorder — but cannot create or delete rows.
  listMenus: (scope?: MenuScope) => {
    const suffix = scope ? `?scope=${encodeURIComponent(scope)}` : ''
    return request<{ items: MenuItem[] }>('/admin/menus' + suffix)
  },
  patchMenu: (id: string, body: MenuPatchInput) =>
    request<{ ok: true }>(`/admin/menus/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  // Announcements / News（统一 CRUD）
  listAnnouncements: (type?: AnnouncementType) => {
    const suffix = type ? `?type=${encodeURIComponent(type)}` : ''
    return request<{ items: AnnouncementRow[] }>('/admin/announcements' + suffix)
  },
  createAnnouncement: (body: AnnouncementCreateInput) =>
    request<{ id: string }>('/admin/announcements', { method: 'POST', body: JSON.stringify(body) }),
  patchAnnouncement: (id: string, body: AnnouncementPatchInput) =>
    request<{ ok: true }>(`/admin/announcements/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteAnnouncement: (id: string) =>
    request<{ ok: true }>(`/admin/announcements/${id}`, { method: 'DELETE' }),

  // Platform config（全局配置开关 / 数值）
  listPlatformConfig: () =>
    request<{ items: PlatformConfigRow[] }>('/admin/platform-config'),
  createPlatformConfig: (body: PlatformConfigCreateInput) =>
    request<{ id: string }>('/admin/platform-config', { method: 'POST', body: JSON.stringify(body) }),
  patchPlatformConfig: (id: string, body: PlatformConfigPatchInput) =>
    request<{ ok: true }>(`/admin/platform-config/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deletePlatformConfig: (id: string) =>
    request<{ ok: true }>(`/admin/platform-config/${id}`, { method: 'DELETE' }),

  // R2 upload — multipart, so bypass the JSON request() wrapper.
  uploadImage: async (file: File, prefix?: string) => {
    const fd = new FormData()
    fd.append('file', file)
    if (prefix) fd.append('prefix', prefix)
    const res = await fetch(API_BASE + '/admin/uploads', {
      method: 'POST',
      body: fd,
      credentials: 'include',
    })
    const text = await res.text()
    let data: unknown = null
    try { data = text ? JSON.parse(text) : null } catch { data = text }
    if (!res.ok) throw new ApiError(res.status, data)
    return data as { key: string; url: string; size: number; contentType: string }
  },

  // Charts + activity
  timeseries: (days = 14) =>
    request<{
      days: number
      series: Array<{
        day: string
        count: number
        paidCount: number
        /** 跨币种相加 —— 仅给 sparkline 趋势，业务上无意义。 */
        paidCents: number
        /** 按币种拆分，UI 用这两个值显示 ¥xxx/$xxx */
        paidCentsCny: number
        paidCentsUsd: number
      }>
    }>(`/admin/stats/timeseries?days=${days}`),
  listEvents: (q: { type?: string; limit?: number } = {}) => {
    const qs = new URLSearchParams()
    if (q.type) qs.set('type', q.type)
    if (q.limit !== undefined) qs.set('limit', String(q.limit))
    const suffix = qs.toString() ? '?' + qs.toString() : ''
    return request<{ events: AdminEvent[] }>('/admin/events' + suffix)
  },

  // ─── Chat (admin) ────────────────────────────────────────────────────
  chatWsToken: () => request<{ token: string }>('/chat/admin/ws-token', { method: 'POST' }),
  chatConversations: () => request<{
    conversations: Array<{
      id: string
      userId: string
      lastMsgAt: number
      unreadAdmin: number
      email: string | null
      displayName: string | null
    }>
  }>('/chat/admin/conversations'),
  chatMessages: (cid: string, opts: { before?: number; limit?: number } = {}) => {
    const qs = new URLSearchParams()
    if (opts.before) qs.set('before', String(opts.before))
    if (opts.limit) qs.set('limit', String(opts.limit))
    const suffix = qs.toString() ? '?' + qs.toString() : ''
    return request<{ messages: Array<{
      id: string; conversationId: string; sender: 'user'|'admin'; kind: 'text'|'image'; body: string; createdAt: number
    }> }>(`/chat/admin/messages/${encodeURIComponent(cid)}${suffix}`)
  },
  chatUploadInit: (cid: string, contentType: string, size: number) =>
    request<{ key: string; uploadUrl: string }>('/chat/admin/upload-init', {
      method: 'POST',
      body: JSON.stringify({ conversationId: cid, contentType, size }),
    }),

  // ─── Data sync (admin) ──────────────────────────────────────────────
  // 数据同步页面 —— 读取 DB_CRAWLER (fuelio-crawler-db) 里的镜像表，
  // 不直接写 fuelio-prod 主库。当 binding 未配（生产 D1 还没 create 时）
  // /sources 端点会优雅退化为 stats: null，UI 也能渲染。
  dataSyncSources: () =>
    request<{ sources: DataSyncSource[] }>('/admin/data-sync/sources'),
  dataSyncZzshuCategories: () =>
    request<{ categories: ZzshuCategoryRow[] }>('/admin/data-sync/zzshu/categories'),
  dataSyncZzshuCommodities: (q: { category?: number; q?: string; stockState?: number } = {}) => {
    const qs = new URLSearchParams()
    if (q.category !== undefined) qs.set('category', String(q.category))
    if (q.q) qs.set('q', q.q)
    if (q.stockState !== undefined) qs.set('stockState', String(q.stockState))
    const suffix = qs.toString() ? '?' + qs.toString() : ''
    return request<{ commodities: ZzshuCommodityRow[] }>('/admin/data-sync/zzshu/commodities' + suffix)
  },
  dataSyncZzshuCommodity: (id: number) =>
    request<{ commodity: ZzshuCommodityFullRow }>(`/admin/data-sync/zzshu/commodities/${id}`),
  dataSyncZzshuImport: (body: DataSyncImportInput) =>
    request<{ ok: true; productId: string; sku: string; linkId: string }>(
      '/admin/data-sync/zzshu/import',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  dataSyncZzshuResync: (commodityId: number) =>
    request<{ ok: true; productId: string; sku: string; syncedPrice: number | null; syncedStock: number | null; syncedAt: number }>(
      '/admin/data-sync/zzshu/resync',
      { method: 'POST', body: JSON.stringify({ commodityId }) },
    ),
  dataSyncZzshuUnlink: (commodityId: number) =>
    request<{ ok: true }>(`/admin/data-sync/zzshu/link/${commodityId}`, { method: 'DELETE' }),

  // ── aibijia ────────────────────────────────────────────────────────
  dataSyncAibijiaProducts: () =>
    request<{ products: AibijiaProductRow[] }>('/admin/data-sync/aibijia/products'),
  dataSyncAibijiaOffers: (q: { product?: string; platform?: string; status?: string } = {}) => {
    const qs = new URLSearchParams()
    if (q.product) qs.set('product', q.product)
    if (q.platform) qs.set('platform', q.platform)
    if (q.status) qs.set('status', q.status)
    const suffix = qs.toString() ? '?' + qs.toString() : ''
    return request<{ offers: AibijiaOfferRow[]; platforms: string[] }>('/admin/data-sync/aibijia/offers' + suffix)
  },
  dataSyncAibijiaOffer: (id: string) =>
    request<{ offer: AibijiaOfferFullRow }>(`/admin/data-sync/aibijia/offers/${encodeURIComponent(id)}`),
  dataSyncAibijiaImport: (body: DataSyncAibijiaImportInput) =>
    request<{ ok: true; productId: string; sku: string; linkId: string }>(
      '/admin/data-sync/aibijia/import',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  dataSyncAibijiaResync: (offerId: string) =>
    request<{ ok: true; productId: string; sku: string; syncedPrice: number | null; syncedStock: number | null; syncedAt: number }>(
      '/admin/data-sync/aibijia/resync',
      { method: 'POST', body: JSON.stringify({ offerId }) },
    ),
  dataSyncAibijiaUnlink: (offerId: string) =>
    request<{ ok: true }>(`/admin/data-sync/aibijia/link/${encodeURIComponent(offerId)}`, { method: 'DELETE' }),
  /** 触发 LDXP 详情抓取（pay.ldxp.cn 的 /shopApi/Shop/goodsInfo JSON API） */
  dataSyncAibijiaEnrichLdxp: (urls?: string[]) =>
    request<{
      attempted: number; succeeded: number; failed: number;
      errors: Array<{ url: string; reason: string }>;
      allWafBlocked?: boolean;
      hint?: string;
    }>('/admin/data-sync/aibijia/enrich-ldxp', {
      method: 'POST',
      body: JSON.stringify(urls ? { urls } : {}),
    }),
  /** 触发 beibei 详情抓取（bei-bei.shop Next.js 页面 → 解析 RSC payload） */
  dataSyncAibijiaEnrichBeibei: (urls?: string[]) =>
    request<{
      attempted: number; succeeded: number; failed: number;
      errors: Array<{ url: string; reason: string }>;
      allNetworkLost?: boolean;
      hint?: string;
    }>('/admin/data-sync/aibijia/enrich-beibei', {
      method: 'POST',
      body: JSON.stringify(urls ? { urls } : {}),
    }),
}

// Trigger a CSV download by hitting the export endpoint and saving the
// blob — uses native fetch (not the JSON wrapper above) because we need the
// raw bytes plus the server's Content-Disposition filename.
export async function downloadCsv(path: string, fallbackName: string): Promise<void> {
  const res = await fetch(API_BASE + path, { credentials: 'include' })
  if (!res.ok) throw new ApiError(res.status, await res.text())
  const cd = res.headers.get('Content-Disposition') ?? ''
  const m = /filename="?([^";]+)"?/.exec(cd)
  const name = m?.[1] ?? fallbackName
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/** Cover-tag color preset keys. Mirror server enum + marketing CSS classes. */
export type CoverTagColor = 'orange' | 'red' | 'green' | 'blue' | 'yellow' | 'grey' | 'purple' | 'pink'
export const COVER_TAG_COLORS: CoverTagColor[] = ['orange', 'red', 'green', 'blue', 'yellow', 'grey', 'purple', 'pink']

export interface AdminProduct {
  id: string
  sku: string
  type: 'plan' | 'physical' | 'topup'
  nameI18n: Record<string, string>
  /** Legacy single-currency price; used as fallback when locale-specific is null. */
  priceCents: number
  /** Legacy currency; matches priceCents. */
  currency: string
  /** Locale-specific prices. Either may be null if not yet set by admin. */
  priceUsdCents: number | null
  priceCnyCents: number | null
  active: boolean
  metadata: Record<string, unknown> | null
  /** Cover chip text. Empty/null = no chip rendered. */
  coverTag: string | null
  /** Color preset for the chip. null = falls back to 'orange' on render. */
  coverTagColor: CoverTagColor | null
  createdAt: number
}

export interface ProductCreateInput {
  sku: string
  type: 'plan' | 'physical' | 'topup'
  nameI18n: Record<string, string>
  priceCents: number
  currency?: string
  /** Optional. When set, takes precedence over priceCents in customer UI. */
  priceUsdCents?: number | null
  priceCnyCents?: number | null
  active?: boolean
  metadata?: Record<string, unknown>
  coverTag?: string | null
  coverTagColor?: CoverTagColor | null
}

export interface AdminUser {
  id: string
  email: string
  displayName: string | null
  status: 'active' | 'disabled' | 'pending'
  /** Role code (matches dict_items where dict_key='user_role'). Drives admin
   *  gating: only role='admin' can sign into this app. Unknown values are
   *  rendered with a "(legacy: x)" fallback. */
  role: string
  locale: string
  emailVerifiedAt: number | null
  referralCode: string
  referredBy: string | null
  /** Wallet balance in cents. UI labels it 「金币」（1 coin = ¥1). */
  walletCents: number
  createdAt: number
}

export interface AdminOrderRow {
  id: string
  userId: string
  status: 'pending' | 'paid' | 'fulfilled' | 'refunded' | 'cancelled'
  amountCents: number
  currency: string
  paymentProvider: string
  createdAt: number
  paidAt: number | null
  userEmail: string | null
}

export interface AdminOrderItem {
  id: string
  orderId: string
  productId: string
  quantity: number
  unitPriceCents: number
}

export interface AdminEvent {
  id: string
  userId: string | null
  type: string
  payload: any
  ip: string | null
  ua: string | null
  createdAt: number
}

// ─── Policy templates ─────────────────────────────────────────────
export type PolicyTemplateType = 'delivery' | 'address' | 'notice'

export interface DeliveryRow {
  key: string
  value: string
  tone?: 'accent' | 'muted'
}
export interface DeliveryBody {
  rowsI18n: Record<string, DeliveryRow[]>
  hintI18n?: Record<string, string>
}

export interface AddressCell {
  label: string
  value: string
  sub?: string
}
export interface AddressBody {
  cellsI18n: Record<string, AddressCell[]>
  ipNoteI18n?: Record<string, string>
  /** Optional per-locale Markdown override. When the current locale has a
   *  non-empty entry, the storefront renders it as rich text **instead of**
   *  the cells/ipNote layout. Lets admins ship long-form 使用说明 with
   *  sections / lists / code blocks without redesigning a structured shape.
   *  Fallback order on render: markdownI18n[lang] → cells (legacy). */
  markdownI18n?: Record<string, string>
}

export interface NoticeItem {
  kind: 'warn' | 'block'
  title: string
  body: string
}
export interface NoticeBody {
  itemsI18n: Record<string, NoticeItem[]>
}

export type PolicyBody = DeliveryBody | AddressBody | NoticeBody

export interface PolicyTemplateRow {
  id: string
  slug: string
  type: PolicyTemplateType
  titleI18n: Record<string, string>
  body: PolicyBody | null
  createdAt: number
  updatedAt: number
}

export type PolicyTemplateCreateInput =
  | { type: 'delivery'; slug: string; titleI18n: Record<string, string>; body: DeliveryBody }
  | { type: 'address';  slug: string; titleI18n: Record<string, string>; body: AddressBody }
  | { type: 'notice';   slug: string; titleI18n: Record<string, string>; body: NoticeBody }

export interface PolicyTemplatePatchInput {
  slug?: string
  titleI18n?: Record<string, string>
  body?: PolicyBody
}

// Dictionaries: variant (订单类型), cat (商品分类), brand (品牌). The table
// is generic, but the admin screen pins these three keys via tabs.
export type DictKey = 'variant' | 'cat' | 'brand'

export interface DictItem {
  id: string
  dictKey: string
  code: string
  labelZh: string
  labelEn: string | null
  sortOrder: number
  active: number
  createdAt: number
  updatedAt: number
}

export interface DictCreateInput {
  dictKey: string
  code: string
  labelZh: string
  labelEn?: string | null
  sortOrder?: number
  active?: 0 | 1
}

export interface DictPatchInput {
  code?: string
  labelZh?: string
  labelEn?: string | null
  sortOrder?: number
  active?: 0 | 1
}

// Menus: thin presentation overlay over code-defined routes.
export type MenuScope = 'marketing' | 'workspace' | 'admin'

export interface MenuItem {
  id: string
  scope: MenuScope
  code: string
  parentId: string | null
  labelZh: string
  labelEn: string | null
  href: string | null
  sortOrder: number
  visible: number
  active: number
  createdAt: number
  updatedAt: number
}

export interface MenuPatchInput {
  labelZh?: string
  labelEn?: string | null
  sortOrder?: number
  visible?: 0 | 1
  active?: 0 | 1
}

// Announcements + News（统一表）
export type AnnouncementType = 'news' | 'announcement'
export type AnnouncementPopupMode = 'none' | 'always' | 'daily' | 'once'
export type AnnouncementColor = 'brand' | 'info' | 'success' | 'warn' | 'danger'

export interface AnnouncementRow {
  id: string
  type: AnnouncementType
  tag: string
  colorVariant: AnnouncementColor
  titleZh: string
  titleEn: string | null
  bodyZh: string
  bodyEn: string | null
  popupMode: AnnouncementPopupMode
  startsAt: number
  endsAt: number | null
  active: number
  createdAt: number
  updatedAt: number
}

export interface AnnouncementCreateInput {
  type: AnnouncementType
  tag?: string
  colorVariant?: AnnouncementColor
  titleZh: string
  titleEn?: string | null
  bodyZh: string
  bodyEn?: string | null
  popupMode?: AnnouncementPopupMode
  startsAt: number
  endsAt?: number | null
  active?: 0 | 1
}

export interface AnnouncementPatchInput extends Partial<AnnouncementCreateInput> {}

// Platform config（全局开关 / 数值）
export type PlatformConfigValueType = 'string' | 'number' | 'boolean' | 'json'

export interface PlatformConfigRow {
  id: string
  code: string
  value: string
  valueType: PlatformConfigValueType
  description: string
  sortOrder: number
  active: number
  updatedBy: string | null
  createdAt: number
  updatedAt: number
}

export interface PlatformConfigCreateInput {
  code: string
  value?: string
  valueType?: PlatformConfigValueType
  description?: string
  sortOrder?: number
  active?: 0 | 1
}

export interface PlatformConfigPatchInput extends Partial<PlatformConfigCreateInput> {}

// ─── Data sync (zzshu mirror) ─────────────────────────────────────────────
//
// 这些行存在 DB_CRAWLER (fuelio-crawler-db) 里，跟 fuelio-prod 主库隔离。
// 主键 id 就是上游 zzshu 的 id（不重新生成 ULID）。
// 价格存 cents（× 100），跟 fuelio.products 一致。

export interface DataSyncSourceStats {
  commodityCount: number
  categoryCount: number
  lastSyncedAt: number | null   // unix sec
}

export interface DataSyncSource {
  code: string                  // 'zzshu' / 'ldxp' / ...
  name: string                  // 中文显示名
  baseUrl: string
  status: 'active' | 'paused'
  description: string
  stats: DataSyncSourceStats | null   // null = D1 binding 还没配 / 表不存在
}

export interface ZzshuCategoryRow {
  id: number
  name: string
  icon: string | null
  sort: number
  pid: number | null
  status: number
  hide: number
  commodity_count: number
  synced_at: number
}

export interface ZzshuCommodityRow {
  id: number
  category_id: number | null
  name: string
  cover: string | null          // 绝对 URL
  price_cents: number
  user_price_cents: number
  stock: number
  stock_state: 0 | 1 | 2         // 0=缺货 / 1=少量 / 2=充足
  order_sold: number
  status: number
  hide: number
  recommend: number
  description_md: string | null
  source_url: string | null
  detail_fetched_at: number | null
  synced_at: number
  // LEFT JOIN data_sync_links —— 已导入则有值，未导入则为 null
  fuelio_product_id: string | null
  fuelio_sku: string | null
  link_synced_at: number | null     // 最近一次 resync 时间（导入时为 null）
  auto_sync_price: number | null
  auto_sync_stock: number | null
}

export interface DataSyncImportInput {
  commodityId: number
  sku: string                          // 大写字母/数字/_/-
  type: 'plan' | 'physical' | 'topup'
  nameZh: string
  nameEn?: string
  priceCnyCents: number                // CNY 分
  priceUsdCents?: number | null        // USD 分（可选）
  descriptionMd: string
  active: boolean
  autoSyncPrice: boolean
  autoSyncStock: boolean
  coverUrl?: string                    // 默认 hot-link 上游 cover；可填 R2 URL
}

export interface ZzshuCommodityFullRow extends ZzshuCommodityRow {
  description_html_raw: string | null
  raw: string | null            // 上游原始 JSON
  inventory_hidden: number
  delivery_way: number
  shared_id: number | null
  owner: number | null
}

// ─── Data sync (aibijia mirror) ───────────────────────────────────────────
//
// aibijia.org 是个聚合站，自己从 LDXP / RedeemGPT / Auto Subscribe / beibei
// 四家爬完后开放 products.json。fuelio 这边消费它的结果，不再单独爬 4 家。
// 数据维度比 zzshu 粗：每条 offer 只有"价格 / 状态(in_stock/out_of_stock)"，
// 没有具体库存数、详情 HTML、主图。所以不做 "导入到 fuelio products" 流水。

export interface AibijiaProductRow {
  slug: string                  // 'chatgpt' / 'gemini' / 'claude-code' / 'grok'
  name: string
  summary: string | null
  category: string | null
  upstream_updated_at: string | null   // ISO 8601
  offers_count: number
  synced_at: number             // unix sec
}

export interface AibijiaOfferRow {
  id: string                    // 我们生成的 ULID（每次 import 都会换）
  product_slug: string
  platform_name: string | null  // 'LDXP' / 'RedeemGPT' / 'Auto Subscribe' / 'beibei'
  source_store_name: string | null
  source_title: string | null
  price_cents: number | null
  currency: string | null
  status: string | null         // 'in_stock' / 'low_stock' / 'out_of_stock'
  url: string | null            // 上游商品 URL ← 这才是稳定的链接外键
  upstream_updated_at: string | null
  display_tags: string | null   // JSON array string
  synced_at: number
  // LEFT JOIN data_sync_links —— 已导入则有值。链接键是 url（不是 id）
  fuelio_product_id: string | null
  fuelio_sku: string | null
  link_synced_at: number | null
  auto_sync_price: number | null
  auto_sync_stock: number | null
  // 富化标记（只在 offers 列表里 LEFT JOIN 出 enriched_at 时填）
  enriched_at?: number | null
}

export interface AibijiaOfferFullRow extends AibijiaOfferRow {
  raw: string | null            // 完整 offer JSON
  // Enrichment (LEFT JOIN aibijia_offer_enrichments)。null = 还没跑 enrich-ldxp。
  enriched_description_md: string | null
  enriched_cover_url: string | null
  enriched_name: string | null
  enriched_price_cents: number | null
  enriched_fetched_at: number | null
  enriched_source: string | null      // 'ldxp' / 'kapay' / ...
}

export interface DataSyncAibijiaImportInput {
  offerId: string                     // aibijia_offers.id (本次 import 的 ULID)
  sku: string
  type: 'plan' | 'physical' | 'topup'
  nameZh: string
  nameEn?: string
  priceCnyCents: number
  priceUsdCents?: number | null
  descriptionMd: string
  active: boolean
  autoSyncPrice: boolean
  autoSyncStock: boolean
  coverUrl?: string
}
